import { Component, type ErrorInfo, type ReactNode } from 'react';
import { focusLater, pageHeading } from '../focus';
import { isChunkError, resetFailedChunks } from '../lazyRetry';
import { Icon } from './Icon';
import Illustration from './Illustration';

/**
 * 오류 경계 (리뷰 8회차 관찰 ⑦) — 한 화면의 그리기 오류가 앱 전체를 흰 화면으로 만들지 않게.
 * 상단바 · 아래 탭 막대는 그대로 두고 본문 자리에만 「이 화면을 그리지 못했습니다」 + 다시 시도 · 홈으로.
 * 주소가 바뀌면(resetKey — 경로 + 질의. Codex 검토: 경로만 보면 /search?q=A 오류 뒤 q 만 바꿔도 풀리지 않았다) 저절로 풀린다. 오류 원문은 콘솔에만 남긴다(화면에 영문 스택을 내보내지 않는다).
 * 14회차 m63 · N30: 화면 파일(지연 청크)을 못 받은 것이면 원인을 따로 말하고, 「다시 시도」 · 주소 바뀜이 받지 못한 청크를 **새로 받게** 한다(lazyRetry).
 */
interface Props { resetKey: string; children: ReactNode }
interface State { failed: boolean; chunk: boolean }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, chunk: false };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, chunk: isChunkError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[화면 오류]', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) {
      resetFailedChunks();
      this.setState({ failed: false, chunk: false });
    }
  }

  private retry = (): void => {
    // 받지 못한 청크는 새 lazy 로 — 다음 그리기에서 주소 끝에 시도 번호를 붙여 실제로 다시 받는다
    resetFailedChunks();
    this.setState({ failed: false, chunk: false });
    // 누른 버튼은 사라진다 — 다시 그린 화면의 제목으로(골격 안 임시 제목은 건너뛴다), 또 못 받았으면 새 「다시 시도」 로
    focusLater(() => document.querySelector<HTMLElement>('main .error-state .btn.primary') ?? pageHeading(), undefined, { tries: 500 });
  };

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const chunk = this.state.chunk;
    return (
      <div className="state empty error-state" role="alert">
        <Illustration name="error-server" />
        <h1 className="state-title">{chunk ? '화면 파일을 받지 못했습니다' : '이 화면을 그리지 못했습니다'}</h1>
        <p>
          {chunk
            ? '이 화면을 그리는 파일을 서버에서 받지 못했습니다 — 연결이 잠깐 끊겼거나 새 버전이 배포된 직후일 수 있습니다. 「다시 시도」 를 누르면 파일을 새로 받습니다. 다른 화면은 그대로 쓸 수 있습니다.'
            : '받은 자료를 화면에 옮기는 중 문제가 생겼습니다. 다시 시도해도 같으면 홈에서 다시 찾아 주세요 — 다른 화면은 그대로 쓸 수 있습니다.'}
        </p>
        <div className="state-actions">
          <button type="button" className="btn sm primary" onClick={this.retry}>
            <Icon name="refresh" size={16} /> 다시 시도
          </button>
          <a className="btn sm" href="/"><Icon name="home" size={16} /> 홈으로</a>
        </div>
      </div>
    );
  }
}
